import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { FaqContentType } from '@lib/data/faq'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import date from '@lib/utils/date.utils'
import Link from 'next/link'
import { msgT } from '@lib/utils/i18n'

const BlackFridayCodingCampSeo = formatSeoConfig({
  title: `Black Friday Coding Camp Deals for Kids & Teens | Codingal`,
  description: `Unlock the biggest Black Friday savings on Codingal's live online camps. Kids and teens build games, apps, and AI projects while grabbing limited-time deals on STEM.org accredited courses.`,
  url: 'https://www.codingal.com/kids-teens-coding-camps/black-friday/',
  metaImage: 'https://www.codingal.com/images/coding-camp/black-friday-og.jpg',
})

const couponCode = 'BLACKFRIDAY50'
const year = date().year()

export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is Codingal's Black Friday Coding Camp ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal&apos;s Black Friday Coding Camp {year} is a live online sprint
        where kids and teens build holiday-ready games, apps, and AI projects
        while unlocking the biggest savings of the year. Sessions blend core
        coding skills with festive creativity so learners stay inspired through
        the sale weekend.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal's Black Friday Coding Camp ${year} is a live online sprint where kids and teens build holiday-ready games, apps, and AI projects while unlocking the biggest savings of the year. Sessions blend core coding skills with festive creativity so learners stay inspired through the sale weekend.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which age groups can attend the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        The Black Friday camp welcomes learners ages 7-18. We group students in
        elementary, middle, and high-school cohorts so instruction feels age
        appropriate and every child gets the support they need.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The Black Friday camp welcomes learners ages 7-18. We group students in elementary, middle, and high-school cohorts so instruction feels age appropriate and every child gets the support they need.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is prior coding experience required to join the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        No. Beginners and advanced coders are both welcome. Instructors start
        with core fundamentals and layer in stretch challenges tailored to each
        learner&apos;s skills and interests.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No. Beginners and advanced coders are both welcome. Instructors start with core fundamentals and layer in stretch challenges tailored to each learner's skills and interests.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What programming languages will participants learn at the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners explore Scratch, Python, Thunkable, Roblox Studio, Lua, HTML,
        CSS, JavaScript, and Data Science depending on the track you choose for
        Black Friday.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners explore Scratch, Python, Thunkable, Roblox Studio, Lua, HTML, CSS, JavaScript, and Data Science depending on the track you choose for Black Friday.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are the Black Friday coding sessions conducted?`,
    }),
    acceptedAnswerText: (
      <>
        Sessions run live on Codingal&apos;s interactive platform with real-time
        instructor guidance. Classes are scheduled around holiday routines, and
        recordings are shared after each lesson so families never miss a beat.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Sessions run live on Codingal's interactive platform with real-time instructor guidance. Classes are scheduled around holiday routines, and recordings are shared after each lesson so families never miss a beat.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is the instructor-to-student ratio at the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        We keep classes small - no more than four learners per instructor - so
        every child receives personalised support, timely feedback, and extra
        guidance on their Black Friday projects.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `We keep classes small - no more than four learners per instructor - so every child receives personalised support, timely feedback, and extra guidance on their Black Friday projects.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What equipment or software does my child need for the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners need a computer or laptop with a reliable internet connection.
        Codingal provides all required software at no extra cost and helps
        families get set up before camp begins.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners need a computer or laptop with a reliable internet connection. Codingal provides all required software at no extra cost and helps families get set up before camp begins.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What projects will my child build during the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Kids and teens create neon Scratch games, e-commerce dashboards in
        Python, Roblox quests packed with holiday surprises, and responsive web
        experiences that showcase Black Friday deals.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Kids and teens create neon Scratch games, e-commerce dashboards in Python, Roblox quests packed with holiday surprises, and responsive web experiences that showcase Black Friday deals.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do you track each learner's progress during the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Instructors review daily activities, assess project submissions, and
        share one-on-one feedback. Progress dashboards help families follow
        along and celebrate wins throughout the Black Friday sprint.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Instructors review daily activities, assess project submissions, and share one-on-one feedback. Progress dashboards help families follow along and celebrate wins throughout the Black Friday sprint.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is technical support available during the Black Friday coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. Our support team is available before, during, and after every
        session to resolve technical issues quickly, keeping the Black Friday
        learning experience smooth even if you are on the go.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Our support team is available before, during, and after every session to resolve technical issues quickly, keeping the Black Friday learning experience smooth even if you are on the go.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What additional support do families receive beyond the Black Friday sessions?`,
    }),
    acceptedAnswerText: (
      <>
        Lifetime access to lesson materials, invitations to coding challenges
        and hackathons, unlimited doubt-clearing sessions, and 24/7 customer
        support so the learning continues after the sale ends.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Lifetime access to lesson materials, invitations to coding challenges and hackathons, unlimited doubt-clearing sessions, and 24/7 customer support so the learning continues after the sale ends.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why should I enroll during the Black Friday sale?`,
    }),
    acceptedAnswerText: (
      <>
        Black Friday pricing unlocks up to 55% savings on Codingal camps, plus
        access to exclusive workshops and readiness guides that keep momentum
        going into the new year. It is the lowest price we offer all season.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Black Friday pricing unlocks up to 55% savings on Codingal camps, plus access to exclusive workshops and readiness guides that keep momentum going into the new year. It is the lowest price we offer all season.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I register for Codingal's Black Friday Coding Camp?`,
    }),
    acceptedAnswerText: (
      <>
        Pick the track that matches your child&apos;s interests, complete the
        online sign-up form, and apply the BLACKFRIDAY50 coupon to lock in your
        deal. We send a welcome kit with schedules, meeting links, and setup
        instructions right after checkout.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Pick the track that matches your child's interests, complete the online sign-up form, and apply the BLACKFRIDAY50 coupon to lock in your deal. We send a welcome kit with schedules, meeting links, and setup instructions right after checkout.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is the Black Friday Coding Camp available online?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. All Black Friday Coding Camp sessions are hosted live online with
        collaborative tools so learners can participate from anywhere in the
        world. Recordings are provided for every lesson.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. All Black Friday Coding Camp sessions are hosted live online with collaborative tools so learners can participate from anywhere in the world. Recordings are provided for every lesson.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is Codingal's refund policy?`,
    }),
    acceptedAnswerText: (
      <>
        Your satisfaction matters to us. If the Black Friday Coding Camp does
        not meet expectations, you can cancel and receive a full refund. Email{' '}
        <a
          href="mailto:support@codingal.com"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          support@codingal.com
        </a>{' '}
        to request a refund for any remaining classes - no questions asked.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Your satisfaction matters to us. If the Black Friday Coding Camp does not meet expectations, you can cancel and receive a full refund. Email support@codingal.com to request a refund for any remaining classes - no questions asked.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why should I trust Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal was founded by experienced edtech entrepreneurs, IIT alumni,
        and former leaders from Google and Amazon, with backing from investors
        like Y Combinator and Rebright Partners. Learn more about us{' '}
        <Link href="/about-us" prefetch={false}>
          <a className="text-blue-200 underline">here</a>
        </Link>
        . We back every program with a money-back guarantee.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal was founded by experienced edtech entrepreneurs, IIT alumni, and former leaders from Google and Amazon, with backing from investors like Y Combinator and Rebright Partners. Learn more about us here. We back every program with a money-back guarantee.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How did Codingal get its name?`,
    }),
    acceptedAnswerText: (
      <>
        The name &ldquo;Codingal&rdquo; combines &ldquo;coding&rdquo; and
        &ldquo;magical&rdquo; to capture how we make coding feel inspiring and
        enjoyable for kids and teens.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The name "Codingal" combines "coding" and "magical" to capture how we make coding feel inspiring and enjoyable for kids and teens.`,
    }),
  },
]

const BlackFridayCodingCampPage = () => {
  return (
    <>
      <NextSeo {...BlackFridayCodingCampSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText:
              typeof acceptedAnswerTextString === 'string'
                ? acceptedAnswerTextString
                : msgT(acceptedAnswerTextString),
            questionName:
              typeof questionName === 'string'
                ? questionName
                : msgT(questionName),
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        breadcrumbItems={[
          { name: 'Codingal', link: '/' },
          { name: 'Coding camps', link: '/kids-teens-coding-camps/' },
          {
            name: 'Black Friday camp',
            link: '/kids-teens-coding-camps/black-friday/',
          },
        ]}
        title={
          <>
            <Trans>Black Friday Coding Camp for Kids &amp; Teens</Trans> {year}
          </>
        }
        description={
          <>
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                💥 Grab the biggest savings of the year. Codingal&apos;s Black
                Friday Coding Camp turns blockbuster deals into blockbuster
                projects in Scratch, Python, Web &amp; App Development, and
                Roblox.
              </Trans>
            </p>
            <p className="text-center lg:text-left pb-2 text-sm lg:text-base">
              <Trans>
                🛍️ Learners collaborate with expert instructors, unlock
                exclusive worksheets, and demo their creations at the end of the
                sale. Seats disappear fast—secure your child&apos;s spot before
                the Black Friday timer ends.
              </Trans>
            </p>
          </>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={75}
        offerPercentage={50}
        imagePath="/images/coding-camp/black-friday-first-fold-image"
        imageAlt="Black Friday coding camp for kids and teens with Codingal"
        backgroundColour="radial-gradient(circle at 20% 15%, rgba(255, 89, 94, 0.35) 0%, rgba(255, 89, 94, 0) 55%), radial-gradient(circle at 80% 10%, rgba(255, 209, 102, 0.4) 0%, rgba(255, 209, 102, 0) 50%), linear-gradient(135deg, #120f2d 0%, #1f163f 45%, #3b1f5a 80%, #ff7034 100%)"
        couponCodeTextBg="radial-gradient(circle at 20% 15%, rgba(255, 89, 94, 0.25) 0%, rgba(255, 89, 94, 0) 100%), linear-gradient(135deg, #120f2d 0%, #1f163f 50%, #3b1f5a 100%)"
      />
      <Partners />
      <Courses
        couponCode={couponCode}
        title={
          <Trans>
            Hand-picked Black Friday Coding Camps tailored to your child
          </Trans>
        }
      />
      <VideoTestimonial
        title="What parents and students say about our Black Friday coding camp"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview isCampPage />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={
          <Trans>Exclusive benefits of our Black Friday Coding Camp</Trans>
        }
      />
      <MoneyBackGuarantee
        isCodingCampPage
        title="Why parents choose Codingal's Black Friday coding camp"
      />
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 89, 94, 0.55) 0%, rgba(255, 89, 94, 0) 40%), radial-gradient(circle at 80% 80%, rgba(255, 209, 102, 0.55) 0%, rgba(255, 209, 102, 0) 50%), linear-gradient(135deg, #130f2d 0%, #1a193e 50%, #2a1f52 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                A Black Friday break packed with{' '}
                <span className="yellow-underline">
                  creativity, code, and celebration
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🎮 Learners speed-run engaging challenges, from gamified
                    logic quests to design sprints that showcase holiday deal
                    pages. Every module keeps the pace fast and fun.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    ⚡ Mixed-age breakout rooms ensure each coder is stretched
                    at the right difficulty level while collaborating with peers
                    across the globe.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-activities"
            imageWidth={370}
            imageHeight={373}
            textColorClassName="text-white"
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 85%, rgba(255, 148, 77, 0.65) 0%, rgba(255, 148, 77, 0) 40%), radial-gradient(circle at 80% 20%, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 50%), linear-gradient(140deg, #1b123d 0%, #281a52 55%, #3d2c78 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                STEM.org Accredited{' '}
                <span className="yellow-underline">Educational Experience</span>{' '}
                and Certificates
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🏅 Every Black Friday cohort follows Codingal&apos;s
                    STEM.org accredited curriculum. Learners discover real-world
                    STEM applications while shipping projects calibrated for
                    their grade level.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    📜 Graduates receive STEM.org-recognized certificates plus a
                    highlight reel summarizing their camp achievements—ideal for
                    school submissions and portfolio showcases.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate"
            imageWidth={370}
            imageHeight={370}
            textColorClassName="text-white"
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 110, 64, 0.65) 0%, rgba(255, 110, 64, 0) 40%), radial-gradient(circle at 80% 80%, rgba(209, 255, 104, 0.45) 0%, rgba(209, 255, 104, 0) 50%), linear-gradient(135deg, #151531 0%, #252450 55%, #452a6b 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Unlock your{' '}
                <span className="yellow-underline">
                  skills and earn coding certifications
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🎯 Students master problem solving with hands-on sprints in
                    Scratch, Python automations for flash sales, and web apps
                    that respond in real time to holiday demand.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🪪 STEM.org certifications and instructor feedback outline
                    the next steps so momentum continues after Black Friday.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate-2"
            imageWidth={370}
            imageHeight={370}
            textColorClassName="text-white"
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 85%, rgba(255, 209, 102, 0.6) 0%, rgba(255, 209, 102, 0) 40%), radial-gradient(circle at 80% 30%, rgba(102, 255, 218, 0.45) 0%, rgba(102, 255, 218, 0) 50%), linear-gradient(145deg, #161836 0%, #2a2456 55%, #4d2c7a 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                The best Black Friday camp for{' '}
                <span className="yellow-underline">
                  kids to learn coding basics and fundamentals
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🧠 Our Black Friday Coding Camp keeps fundamentals front and
                    center. Learners build logic, sequencing, and design skills
                    with guidance from mentors who understand how to motivate
                    young coders.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🛠️ Tracks span Scratch, Python, AI, web development, and
                    Roblox with six live lessons per program and focused
                    practice between sessions.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🌟 Families can reuse projects for school showcases, STEM
                    fairs, and holiday demo nights, extending the value long
                    after Black Friday wraps.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-student-learning"
            imageWidth={370}
            imageHeight={373}
            textColorClassName="text-white"
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 176, 96, 0.6) 0%, rgba(255, 176, 96, 0) 40%), radial-gradient(circle at 80% 80%, rgba(138, 228, 255, 0.45) 0%, rgba(138, 228, 255, 0) 50%), linear-gradient(140deg, #151531 0%, #252450 55%, #3f3980 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Our Black Friday courses are appropriate for{' '}
                <span className="yellow-underline">
                  high school, middle school,
                </span>{' '}
                and elementary school students
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    👩‍🎓 Cohorts are organized by grade, so every learner joins a
                    crew that matches their pace. Younger students lean into
                    playful storytelling while teens dive into advanced
                    problem-solving.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🔭 Personalized progress updates and learning pathways help
                    families map what comes next, keeping the excitement alive
                    well beyond Black Friday weekend.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-school-kids"
            imageWidth={370}
            imageHeight={373}
            textColorClassName="text-white"
          />
        </div>
      </section>
      <Faq
        title="Got questions? Find answers in our FAQ section"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default BlackFridayCodingCampPage
